/**
 * 商談の受け取り書類の hooks (2026-10)。
 *
 * - 置いた PDF はまず「未分類」(deals/{dealId}/attachments) として保存する
 * - 書類ごとに後から種類を選ぶと、種類ごとの保存先へ移す (useMoveReceivedDocument)
 *     プロフォーマ   → uploadManufacturerQuote (保存済み PDF を読み、番号と合計を AI 抽出) → manufacturer_quotes
 *     最終インボイス → PurchaseOrder.officialOrderPdfPath
 *     DHL 書類       → PurchaseOrder.importDocuments
 *     未分類         → attachments
 *   PDF ファイル自体は移動・複製せず、同じ Storage パスを各記録が指す
 * - 読込・追加・登録解除はクライアント直接 (firestore.rules: attachments は read viewer+ / create・delete editor+)
 * - PDF は Storage `deals/{dealId}/attachments/{timestamp}-{safeName}` (storage.rules: editor+ 20MB 以下 PDF/画像)
 * - 登録解除は記録の削除のみ。PDF 本体は Storage に残す
 */
import type {
  DealAttachment,
  ImportDocumentType,
  IsoOrTimestamp,
  PurchaseOrder,
  PurchaseOrderImportDocument,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes } from 'firebase/storage';

import { uploadManufacturerQuoteFn } from '@/hooks/useManufacturerQuotes';
import { auth, db, storage } from '@/lib/firebase';

export interface DealAttachmentRecord extends DealAttachment {
  id: string;
}

export function useDealAttachments(dealId: string | null | undefined) {
  return useQuery({
    queryKey: ['deal_attachments', dealId],
    enabled: !!dealId,
    queryFn: async (): Promise<DealAttachmentRecord[]> => {
      if (!dealId) return [];
      const snap = await getDocs(
        query(collection(db, 'deals', dealId, 'attachments'), orderBy('uploadedAt', 'desc')),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as DealAttachment) }));
    },
  });
}

export function useUploadDealAttachments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { dealId: string; files: File[] }) => {
      const uid = auth.currentUser?.uid ?? '';
      for (const file of input.files) {
        const safeName = file.name.replace(/[^\w.-]+/g, '_').slice(0, 80);
        const path = `deals/${input.dealId}/attachments/${Date.now()}-${safeName}`;
        await uploadBytes(storageRef(storage, path), file, { contentType: file.type });
        await addDoc(collection(db, 'deals', input.dealId, 'attachments'), {
          fileName: file.name,
          storagePath: path,
          contentType: file.type,
          uploadedAt: serverTimestamp(),
          uploadedBy: uid,
        });
      }
      return { count: input.files.length };
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['deal_attachments', vars.dealId] }),
  });
}

export function useRemoveDealAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { dealId: string; attachmentId: string }) => {
      await deleteDoc(doc(db, 'deals', input.dealId, 'attachments', input.attachmentId));
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['deal_attachments', vars.dealId] }),
  });
}

// ─── 種類の変更 (保存先の移動) ───

/** 受け取り書類の種類。dhl_duty / dhl_other は DHL 書類の 輸入関税書類 / その他関連書類。 */
export type ReceivedKind = 'other' | 'pf' | 'fin' | 'dhl_duty' | 'dhl_other';

export const RECEIVED_KIND_LABEL: Record<ReceivedKind, string> = {
  other: '未分類',
  pf: 'プロフォーマ',
  fin: '最終インボイス',
  dhl_duty: 'DHL 輸入関税書類',
  dhl_other: 'DHL その他関連書類',
};

const DHL_TYPE: Record<'dhl_duty' | 'dhl_other', ImportDocumentType> = {
  dhl_duty: 'import_duty',
  dhl_other: 'other',
};

/** いまの保存場所 */
export interface ReceivedSource {
  kind: ReceivedKind;
  storagePath: string;
  fileName: string;
  uploadedAt: IsoOrTimestamp | null;
  uploadedBy: string;
  /** kind = other */
  attachmentId?: string;
  /** kind = pf */
  manufacturerQuoteId?: string;
  /** kind = fin / dhl_* */
  poId?: string;
  /** kind = dhl_* */
  importDocumentId?: string;
}

interface MoveInput {
  dealId: string;
  source: ReceivedSource;
  target: ReceivedKind;
  /** 登録先の発注 (pf は任意、fin / dhl_* は必須) */
  po: { id: string; invoiceId: string | null } | null;
  /** 登録先の発注に最終インボイスが既にある場合、それを「未分類」へ移すための情報 */
  replacedFinal?: {
    storagePath: string;
    fileName: string;
    uploadedAt: IsoOrTimestamp | null;
    uploadedBy: string;
  } | null;
}

interface MoveResult {
  /** プロフォーマにした時の読み取り結果 */
  extracted?: {
    invoiceNumber: string | null;
    totalAmount: number | null;
    extractionError: string | null;
  };
}

function keepTimestamp(v: IsoOrTimestamp | null | undefined): Timestamp {
  if (v instanceof Timestamp) return v;
  if (typeof v === 'string') {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return Timestamp.fromDate(d);
  }
  return Timestamp.now();
}

async function addAttachmentRecord(
  dealId: string,
  f: {
    storagePath: string;
    fileName: string;
    uploadedAt: IsoOrTimestamp | null;
    uploadedBy: string;
  },
) {
  await addDoc(collection(db, 'deals', dealId, 'attachments'), {
    fileName: f.fileName,
    storagePath: f.storagePath,
    contentType: 'application/pdf',
    uploadedAt: keepTimestamp(f.uploadedAt),
    uploadedBy: f.uploadedBy || auth.currentUser?.uid || '',
  });
}

async function rewriteImportDocuments(
  dealId: string,
  poId: string,
  fn: (docs: PurchaseOrderImportDocument[]) => PurchaseOrderImportDocument[],
) {
  const ref = doc(db, 'deals', dealId, 'purchase_orders', poId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const current = ((snap.data() as PurchaseOrder | undefined)?.importDocuments ??
      []) as PurchaseOrderImportDocument[];
    tx.update(ref, { importDocuments: fn(current), updatedAt: serverTimestamp() });
  });
}

/**
 * 書類の種類を変える。新しい保存先に登録してから、元の保存先の記録を外す
 * (途中で失敗しても書類が一覧から消えない順序)。
 */
export function useMoveReceivedDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      dealId,
      source,
      target,
      po,
      replacedFinal,
    }: MoveInput): Promise<MoveResult> => {
      const uid = auth.currentUser?.uid ?? '';
      const result: MoveResult = {};
      const sameDhlPo =
        (source.kind === 'dhl_duty' || source.kind === 'dhl_other') &&
        (target === 'dhl_duty' || target === 'dhl_other') &&
        source.poId === po?.id;

      // DHL 書類の中での種類変更は、同じ発注内の書き換えだけで済む
      if (sameDhlPo && source.poId && source.importDocumentId) {
        await rewriteImportDocuments(dealId, source.poId, (docs) =>
          docs.map((d) =>
            d.id === source.importDocumentId
              ? { ...d, type: DHL_TYPE[target as 'dhl_duty' | 'dhl_other'] }
              : d,
          ),
        );
        return result;
      }

      // ① 新しい保存先に登録
      if (target === 'other') {
        await addAttachmentRecord(dealId, source);
      } else if (target === 'pf') {
        const r = await uploadManufacturerQuoteFn({
          dealId,
          fileName: source.fileName,
          contentType: 'application/pdf',
          invoiceId: po?.invoiceId ?? null,
          storagePath: source.storagePath,
        });
        result.extracted = {
          invoiceNumber: r.data.invoiceNumber,
          totalAmount: r.data.totalAmount,
          extractionError: r.data.extractionError,
        };
      } else if (target === 'fin') {
        if (!po) throw new Error('発注を選んでください');
        if (replacedFinal) await addAttachmentRecord(dealId, replacedFinal);
        await updateDoc(doc(db, 'deals', dealId, 'purchase_orders', po.id), {
          officialOrderPdfPath: source.storagePath,
          officialOrderFileName: source.fileName,
          officialOrderUploadedAt: keepTimestamp(source.uploadedAt),
          officialOrderUploadedBy: source.uploadedBy || uid,
          updatedAt: serverTimestamp(),
        });
      } else {
        if (!po) throw new Error('発注を選んでください');
        const entry: PurchaseOrderImportDocument = {
          id: crypto.randomUUID(),
          type: DHL_TYPE[target],
          storagePath: source.storagePath,
          fileName: source.fileName,
          uploadedAt: keepTimestamp(source.uploadedAt),
          uploadedBy: source.uploadedBy || uid,
        };
        await updateDoc(doc(db, 'deals', dealId, 'purchase_orders', po.id), {
          importDocuments: arrayUnion(entry),
          updatedAt: serverTimestamp(),
        });
      }

      // ② 元の保存先の記録を外す (PDF は残す)
      if (source.kind === 'other' && source.attachmentId) {
        await deleteDoc(doc(db, 'deals', dealId, 'attachments', source.attachmentId));
      } else if (source.kind === 'pf' && source.manufacturerQuoteId) {
        await deleteDoc(
          doc(db, 'deals', dealId, 'manufacturer_quotes', source.manufacturerQuoteId),
        );
      } else if (source.kind === 'fin' && source.poId) {
        // 同じ発注の最終インボイスを置き換えた場合は、①で上書き済み
        if (!(target === 'fin' && po?.id === source.poId)) {
          await updateDoc(doc(db, 'deals', dealId, 'purchase_orders', source.poId), {
            officialOrderPdfPath: null,
            officialOrderFileName: null,
            officialOrderUploadedAt: null,
            officialOrderUploadedBy: null,
            updatedAt: serverTimestamp(),
          });
        }
      } else if (
        (source.kind === 'dhl_duty' || source.kind === 'dhl_other') &&
        source.poId &&
        source.importDocumentId
      ) {
        await rewriteImportDocuments(dealId, source.poId, (docs) =>
          docs.filter((d) => d.id !== source.importDocumentId),
        );
      }
      return result;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['deal_attachments', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['manufacturer_quotes', 'deal', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['manufacturer_quotes', 'all'] });
      qc.invalidateQueries({ queryKey: ['purchase_orders', 'deal', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['purchase_orders', 'all'] });
      qc.invalidateQueries({ queryKey: ['deals', vars.dealId, 'activities'] });
    },
  });
}
